import { ArrowLeftRight, ArrowUp, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "../../components/Button.tsx";
import { ObjectIcon } from "../../components/ObjectIcon.tsx";
import { NNBSP, formatMetres, formatNumber } from "../../i18n/format.ts";
import { useLocale } from "../../i18n/index.ts";
import type { Course } from "./bearing.ts";
import { useCourseResult, useCourseSelection, useDeclination } from "./useCourse.ts";
import type { CourseObject } from "./useCourse.ts";

const MINUS = "−";

interface Props {
  /** Everything a course can start or end at. */
  objects: readonly CourseObject[];
  /** `glass`: floating card in the 3D view; `list`: inset card in the sheet. */
  variant: "glass" | "list";
  /** The 3D view lets the diver tap a pin too. */
  pinHint?: boolean;
}

/**
 * The compass course between two underwater objects, or (with only the start chosen) the list of
 * destinations. The pair lives in the URL (`?kurs=A,B`). Announces changes politely.
 */
export function CoursePanel({ objects, variant, pinHint = false }: Props) {
  const { t } = useTranslation();
  const sel = useCourseSelection();
  const byId = useMemo(() => new Map(objects.map((o) => [o.id, o])), [objects]);
  const a = sel.startId ? (byId.get(sel.startId) ?? null) : null;
  const b = sel.endId ? (byId.get(sel.endId) ?? null) : null;
  if (!a) return null;
  // the same object twice has no course: say so, and offer the others
  const same = b !== null && b.id === a.id;
  const others = objects.filter((o) => o.id !== a.id);

  return (
    <section
      className={`course-card course-card--${variant}${variant === "glass" ? " ds-glass" : ""}`}
      aria-label={t("course.title")}
      data-testid="course-card"
    >
      {b && !same ? (
        <Result a={a} b={b} />
      ) : (
        <Picker a={a} others={others} pinHint={pinHint} same={same} onPick={sel.setEnd} />
      )}
      <IconButton
        tone="plain"
        className="course-card__close"
        aria-label={t("course.close")}
        icon={<X aria-hidden="true" strokeWidth={1.75} />}
        onClick={sel.clear}
      />
    </section>
  );
}

const nameOf = (o: CourseObject, fallback: string) => o.name || fallback;

function Picker({
  a,
  others,
  pinHint,
  same,
  onPick,
}: {
  a: CourseObject;
  others: readonly CourseObject[];
  pinHint: boolean;
  same: boolean;
  onPick: (id: string) => void;
}) {
  const { t } = useTranslation();
  const from = nameOf(a, t(`objects.type.${a.type}`));
  return (
    <div className="course-card__body">
      <h3 className="ds-text-headline m-0">{t("course.from", { name: from })}</h3>
      {same ? (
        <p className="ds-text-subheadline m-0" role="status" data-testid="course-same">
          {t("course.same")}
        </p>
      ) : null}
      <p className="ds-text-subheadline text-label-secondary m-0">
        {t("course.pick")}
        {pinHint ? ` ${t("course.pickPin")}` : ""}
      </p>
      {others.length === 0 ? (
        <p className="ds-text-footnote m-0">{t("course.noOthers")}</p>
      ) : (
        <ul className="course-card__targets" aria-label={t("course.pick")}>
          {others.map((o) => (
            <li key={o.id}>
              <button type="button" className="ds-btn ds-btn--tinted" onClick={() => onPick(o.id)}>
                <ObjectIcon type={o.type} size={16} />
                <span>
                  {nameOf(o, t(`objects.type.${o.type}`))}
                  {o.spotName ? ` · ${o.spotName}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Result({ a, b }: { a: CourseObject; b: CourseObject }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const sel = useCourseSelection();
  const decl = useDeclination(a.location);
  const c = useCourseResult(a, b);
  const nameA = nameOf(a, t(`objects.type.${a.type}`));
  const nameB = nameOf(b, t(`objects.type.${b.type}`));

  return (
    <div className="course-card__body">
      <div className="course-card__route">
        <span className="ds-text-subheadline course-card__names">
          <span>{nameA}</span>
          <span aria-hidden="true"> → </span>
          <span className="sr-only">{` ${t("course.to")} `}</span>
          <span>{nameB}</span>
        </span>
        <IconButton
          tone="plain"
          aria-label={t("course.swap")}
          icon={<ArrowLeftRight aria-hidden="true" strokeWidth={1.75} />}
          onClick={sel.swap}
        />
      </div>
      <div aria-live="polite" aria-atomic="true" className="course-card__live">
        {c ? (
          <Values c={c} locale={locale} approxA={a.approx === true} approxB={b.approx === true} />
        ) : decl.isError ? (
          <p className="ds-text-subheadline m-0">{t("course.unavailable")}</p>
        ) : (
          <p className="ds-text-subheadline text-label-secondary m-0">{t("course.computing")}</p>
        )}
      </div>
      <p className="ds-text-footnote text-label-secondary m-0">{t("course.footnote")}</p>
    </div>
  );
}

const signed = (v: number, locale: ReturnType<typeof useLocale>) =>
  `${v < 0 ? MINUS : "+"}${formatNumber(Math.abs(v), locale, 1)}${NNBSP}m`;

function Values({
  c,
  locale,
  approxA,
  approxB,
}: {
  c: Course;
  locale: ReturnType<typeof useLocale>;
  approxA: boolean;
  approxB: boolean;
}) {
  const { t } = useTranslation();
  // depths from the lakebed model (not measured) carry "≈", the change when either end does
  const mark = (approx: boolean) => (approx ? `≈${NNBSP}` : "");
  const dir = c.declinationDeg < 0 ? t("course.west") : t("course.east");
  const decl = `${c.declinationDeg < 0 ? MINUS : "+"}${formatNumber(Math.abs(c.declinationDeg), locale, 1)}°${NNBSP}${dir}`;
  return (
    <>
      <p className="course-card__compass m-0">
        {/* the glyph shows the direction on a north-up map: the geographic bearing */}
        <ArrowUp
          aria-hidden="true"
          className="course-card__needle"
          strokeWidth={2.25}
          style={{ transform: `rotate(${c.trueDeg}deg)` }}
        />
        <span className="ds-text-headline">{t("course.compass")}</span>{" "}
        <b className="course-card__deg" data-testid="course-compass">
          {c.compassDeg}°
        </b>
      </p>
      <p className="ds-text-footnote text-label-secondary m-0" data-testid="course-true">
        {t("course.true", { deg: c.trueDeg })} · {t("course.declination", { value: decl })}
      </p>
      <dl className="course-card__facts ds-text-subheadline">
        <dt>{t("course.distance")}</dt>
        <dd data-testid="course-distance">{formatMetres(c.distanceM, locale, 0)}</dd>
        <dt>{t("course.depth")}</dt>
        <dd data-testid="course-depth">
          {c.deltaDepthM === null || c.depthA === null || c.depthB === null ? (
            t("course.depthUnknown")
          ) : (
            <>
              {mark(approxA)}
              {formatMetres(c.depthA, locale, 1)} → {mark(approxB)}
              {formatMetres(c.depthB, locale, 1)} ({mark(approxA || approxB)}
              {signed(c.deltaDepthM, locale)})
            </>
          )}
        </dd>
        {c.slopeM !== null ? (
          <>
            <dt>{t("course.slope")}</dt>
            <dd data-testid="course-slope">{formatMetres(c.slopeM, locale, 0)}</dd>
          </>
        ) : null}
        <dt>{t("course.back")}</dt>
        <dd data-testid="course-back">{c.backCompassDeg}°</dd>
      </dl>
    </>
  );
}
